import React, { useState, useEffect, useRef } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import CardContent from '@mui/material/CardContent';
import Divider from '@mui/material/Divider';
import axios from 'axios';
import { splunkFilteredLogsStream } from '../../../utils/Api';

// Define sources array
const sources = [
  { icon: "🔥", label: "Firewalls" },
  { icon: "🛡️", label: "EDR/XDR" },
  { icon: "☁️", label: "Cloud" },
  { icon: "📊", label: "SIEM Agents" },
  { icon: "💻", label: "Applications" },
  { icon: "🌐", label: "Network" },
  { icon: "🔐", label: "Identity" },
  { icon: "📧", label: "Email" },
];

const LiveEventPath2Splunk = () => {
    const [events, setEvents] = useState([]);
    const [isConnected, setIsConnected] = useState(false);
    const eventSourceRef = useRef(null);

    // Function to parse streaming log data into display format
    const parseLogData = (rawLog) => {
      try {
        // Parse the JSON string if needed
        const logData = typeof rawLog === 'string' ? JSON.parse(rawLog) : rawLog;
        
        // For Splunk, the actual log data is in logData.source.result._raw as a JSON string
        const splunkResult = logData.source.result;
        const rawMessage = JSON.parse(splunkResult._raw);
        console.log('Processing Splunk log entry:', rawMessage);
        
        // Use rawMessage as the parsed data
        const parsed = rawMessage;
        console.log('Parsed log data:', parsed);
        // Extract event severity/level for display
        const getSeverityLevel = (level) => {
          const severityMap = {
            0: 'EMERGENCY',
            1: 'ALERT', 
            2: 'CRITICAL',
            3: 'ERROR',
            4: 'WARNING',
            5: 'NOTICE',
            6: 'INFO',
            7: 'DEBUG'
          };
          return severityMap[level] || 'INFO';
        };
        
        const result = {
          id: splunkResult._serial || `${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
          host: parsed.host || splunkResult.host || 'unknown',
          level: getSeverityLevel(parsed.level),
          levelNum: parsed.level || 6,
          message: parsed.RAW_MSG || parsed.message || 'No raw message',
          appName: parsed.appName || 'N/A',
          remoteAddress: parsed.IP_ADDRESS || 'N/A',
          time: parsed.timestamp ? new Date(parsed.timestamp).toLocaleTimeString() : new Date().toLocaleTimeString(),
          tag: parsed.tag || 'system',
          splunkTime: splunkResult._time || 'N/A'
        };
        
        return result;
      } catch (error) {
        console.error('Error parsing Splunk log data:', error);
        console.error('Raw log that caused error:', rawLog);
        
        // Try to create a fallback event with whatever data we have
        let fallbackMessage = 'Unparseable log data';
        let fallbackHost = 'unknown';
        
        try {
          const logData = typeof rawLog === 'string' ? JSON.parse(rawLog) : rawLog;
          
          // Try to extract basic info from Splunk structure
          if (logData.source && logData.source.result) {
            fallbackHost = logData.source.result.host || 'unknown';
            fallbackMessage = logData.source.result._raw || 'No raw data';
            if (fallbackMessage.length > 100) {
              fallbackMessage = fallbackMessage.substring(0, 100) + '...';
            }
          } else if (typeof rawLog === 'string') {
            fallbackMessage = rawLog.length > 100 ? rawLog.substring(0, 100) + '...' : rawLog;
          } else if (rawLog && typeof rawLog === 'object') {
            fallbackMessage = JSON.stringify(rawLog).substring(0, 100) + '...';
          }
        } catch (fallbackError) {
          console.error('Even fallback parsing failed:', fallbackError);
        }
        
        // Return a fallback event if parsing fails
        return {
          id: `${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
          host: fallbackHost,
          level: 'ERROR',
          levelNum: 3,
          message: fallbackMessage,
          appName: 'N/A',
          remoteAddress: 'N/A',
          time: new Date().toLocaleTimeString(),
          tag: 'parsing_error'
        };
      }
    };

    // Stream Splunk logs using Server-Sent Events or WebSocket
    useEffect(() => {
      const connectToStream = async () => {
        try {
          setIsConnected(true);
          
          // Option 1: Server-Sent Events (SSE)
          if (typeof EventSource !== 'undefined') {
            eventSourceRef.current = new EventSource(splunkFilteredLogsStream);
            
            eventSourceRef.current.onopen = () => {
              setIsConnected(true);
            };
            
            eventSourceRef.current.onmessage = (event) => {
              
              try {
                // Parse the incoming data to check what type it is
                const rawData = typeof event.data === 'string' ? JSON.parse(event.data) : event.data;
                
                // Filter out connection/status messages - only process actual log data
                if (rawData.message && rawData.message.includes('Connected to')) {
                  console.log('Connection status message received:', rawData.message);
                  return; // Skip processing connection messages
                }
                
                // Check if this looks like actual Splunk log data (should have source.result._raw structure)
                if (!rawData.source || !rawData.source.result || !rawData.source.result._raw) {
                  console.warn('Received data that is not a Splunk log entry:', rawData);
                  return; // Skip processing non-log data
                }
                
                const parsedEvent = parseLogData(event.data);
                
                setEvents(prevEvents => {
                  const newEvents = [parsedEvent, ...prevEvents].slice(0, 20);
                  return newEvents;
                });
              } catch (parseError) {
                console.error('Failed to parse SSE event:', parseError);
                console.error('Event data that failed:', event.data);
              }
            };
            
            eventSourceRef.current.onerror = (error) => {
              console.error('Stream connection error:', error);
              setIsConnected(false);
            };
          } else {
            // Fallback: Polling method if SSE is not supported
            const pollInterval = setInterval(async () => {
              try {
                const response = await axios.get(splunkFilteredLogsStream);
                if (response.data) {
                  const parsedEvent = parseLogData(response.data);
                  setEvents(prevEvents => {
                    const newEvents = [parsedEvent, ...prevEvents].slice(0, 20);
                    return newEvents;
                  });
                }
              } catch (error) {
                console.error('Error polling Splunk logs:', error);
              }
            }, 2000); // Poll every 2 seconds
            
            return () => clearInterval(pollInterval);
          }
          
        } catch (error) {
          console.error('Error connecting to stream:', error);
          setIsConnected(false);
        }
      };

      connectToStream();

      // Cleanup function
      return () => {
        if (eventSourceRef.current) {
          eventSourceRef.current.close();
          eventSourceRef.current = null;
        }
        setIsConnected(false);
      };
    }, []);

  return (
    <>
        <CardContent sx={{ maxHeight: 620, overflowY: 'auto', p: 2, pb: 4 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                <Typography variant="h6" sx={{ fontWeight: 700, color: '#60a5fa' }}>Live Splunk Logs</Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                </Box>
              </Box>
              <Divider sx={{ mb: 2, borderColor: 'rgba(255,255,255,0.1)' }} />
              {/* Header Row */}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, fontWeight: 700, color: '#b0bec5', mb: 1, position: 'sticky', top: 0, background: 'rgba(10,34,64,0.92)', zIndex: 2, py: 1 }}>
                <Typography variant="caption" sx={{ width: 120, minWidth: 120, maxWidth: 120 }}>Host</Typography>
                <Typography variant="caption" sx={{ flex: 1, minWidth: 0 }}>Raw Message</Typography>
                <Typography variant="caption" sx={{ width: 140, minWidth: 140, maxWidth: 140, textAlign: 'center' }}>Remote Address</Typography>
              </Box>
              {events.length === 0 ? (
                <Box sx={{ textAlign: 'center', mt: 4, p: 3 }}>
                  <Typography variant="body2" sx={{ color: '#b0bec5', mb: 1 }}>
                    {isConnected ? 'Waiting for log events...' : 'No connection to log stream'}
                  </Typography>
                  <Typography variant="caption" sx={{ color: '#666' }}>
                    {isConnected ? 'Stream is active and ready to receive data' : 'Check your API connection'}
                  </Typography>
                </Box>
              ) : (
                events.map((row, i) => {
                  // Get severity color
                  const getSeverityColor = (levelNum) => {
                    if (levelNum <= 2) return '#f44336'; // Critical/Alert - Red
                    if (levelNum <= 4) return '#ff9800'; // Error/Warning - Orange  
                    if (levelNum <= 5) return '#2196f3'; // Notice - Blue
                    return '#4caf50'; // Info/Debug - Green
                  };
                  
                  const severityColor = getSeverityColor(row.levelNum);
                  
                  return (
                    <Box key={`${row.id}_${i}`} sx={{ 
                      mb: 1.5, 
                      display: 'flex', 
                      alignItems: 'center', 
                      gap: 2, 
                      flexWrap: 'wrap',
                      p: 1,
                      borderRadius: 1,
                      backgroundColor: i % 2 === 0 ? 'rgba(255,255,255,0.02)' : 'transparent',
                      '&:hover': {
                        backgroundColor: 'rgba(96,165,250,0.1)',
                        transition: 'background-color 0.2s'
                      }
                    }}>
                      
                      
                      {/* Host */}
                      <Typography 
                        variant="body2" 
                        title={row.host} 
                        sx={{ 
                          color: '#60a5fa', 
                          width: 120,
                          minWidth: 120,
                          maxWidth: 120,
                          fontWeight: 600,
                          fontFamily: 'monospace',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap'
                        }}
                      >
                        {row.host}
                      </Typography>
                      
                      
                      {/* Message */}
                      <Typography 
                        variant="body2" 
                        title={row.message}
                        sx={{ 
                          flex: 1, 
                          minWidth: 0,
                          color: 'white', 
                          fontWeight: 400,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                          cursor: 'help'
                        }}
                      >
                        {row.message}
                      </Typography>
                      
                      {/* Remote Address */}
                      <Typography 
                        variant="caption" 
                        title={`Remote IP: ${row.remoteAddress}`}
                        sx={{ 
                          color: '#ffa726', 
                          width: 140,
                          minWidth: 140,
                          maxWidth: 140,
                          fontFamily: 'monospace',
                          textAlign: 'center',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap'
                        }}
                      >
                        {row.remoteAddress}
                      </Typography>
                      
                      
                    </Box>
                  );
                })
              )}
        </CardContent>
    </>
  )
}

export default LiveEventPath2Splunk